import React, { Suspense, SyntheticEvent, useEffect } from "react";
import {
  SnackbarCloseReason,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material";
import { theme } from "./theme/theme";
import store from "./redux/store";
import { Provider } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch, useAppSelector, useAuth } from "./redux/hooks";
import { Route, Routes, BrowserRouter, useLocation } from "react-router-dom";
import LoginModal from "./components/authentication/LoginModal";
import Notification from "./components/common/Notification";
import ui from "./redux/reducers/uiReducer";
import VmsError from "./components/pages/VmsError";
import { useGetUsersMeQuery } from "./redux/vmsApi";
import LoadingCover from "./components/common/LoadingCover";
import CssBaseline from "@mui/material/CssBaseline";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// @ts-ignore
import HttpsRedirect from "react-https-redirect";
import ProcurementRoutes from "./components/procurement/ProcurementRoutes";
import EmployeeReviewRoutes from "./components/reviews/EmployeeReviewRoutes";
import Dashboard from "./components/pages/Dashboard";
import PrivateRoute from "./components/common/PrivateRoute";
import PropertyRegisterRoutes from "./components/propertyRegister/PropertyRegisterRoutes";
import PayrollGenerator from "./components/payroll/PayrollGenerator";
import InventoryRoutes from "./components/inventory/InventoryRoutes";
import PlantReportsRoutes from "./components/plantReports/PlantReportsRoutes";
import AgreementsRoutes from "./components/agreements/AgreementsRoutes";
import KnowledgeBase from "./components/knowledgeBase/KnowledgeBase";
import AiAssistant from "./components/aiAssistant/AiAssistant";
import config from "./config";
import LogRocket from "logrocket";
import FinanceRoutes from "./components/finance/financeRoutes";
import BomRoutes from "components/bill-of-materials/BomRoutes";
import ContractRoutes from "components/procurement-contracts/ContractRoutes";
import ComsRoutes from "./components/coms/ComsRoutes";
import ResetPasswordPage from "./components/authentication/resetPassword/ResetPasswordPage";
import WallboardRoutes from "./components/wallboard/WallboardRoutes";
import LocationSync from "./components/common/LocationSync";
import { Analytics } from "@vercel/analytics/react";
import { tokenStorage } from "./redux/baseApi";
import UserManagement from "./components/admin/UserManagement";
import BusinessUnitManagement from "./components/admin/BusinessUnitManagement";
// DISABLED: Staff Management module
// import StaffManagementRoutes from "./components/staffManagement/StaffManagementRoutes";

const AccessControlRoutes = React.lazy(
  () => import("./components/accesscontrol/AccessControlRoutes"),
);
const PettyCashPage = React.lazy(
  () => import("./components/finance/pages/PettyCashPage"),
);

const hasValidToken = () => {
  const localStorageToken = tokenStorage.getAccessToken();
  if (localStorageToken && localStorageToken.length > 10) return true;

  const cookieMatch = document.cookie.match("(^| )jwtToken=([^;].+?)(;|$)");
  return !!(cookieMatch && cookieMatch[2] && cookieMatch[2].length > 10);
};

const App = () => {
  const hasToken = hasValidToken();
  const { user } = useAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const actionNotification = useAppSelector(
    (state) => state.ui.actionNotification,
  );
  const closeNotification = (
    event: Event | SyntheticEvent<any, Event>,
    reason: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway" || reason === "timeout") {
      dispatch(ui.actions.removeActionNotification());
    }
  };

  useEffect(() => {
    if (config.logrocket) {
      LogRocket.init("sq4cty/westprop-vms");
      if (user && user.id && user.name && user.email) {
        LogRocket.identify(user.id, { name: user.name, email: user.email });
      }
    }
  }, [user]);

  // Fetch user data if we have a token but no user in Redux state
  const shouldSkip = hasToken && !user ? undefined : skipToken;
  const { isLoading } = useGetUsersMeQuery(shouldSkip);
  if (isLoading) return <LoadingCover />;

  return (
    <ThemeProvider theme={theme}>
      <StyledEngineProvider injectFirst>
        <VmsError className={`App`}>
          <Suspense fallback={<LoadingCover />}>
            {actionNotification && (
              <Notification
                open={actionNotification.show}
                title={actionNotification.notification.title}
                message={actionNotification.notification.description}
                onClose={closeNotification}
                position={{ vertical: "bottom", horizontal: "left" }}
              />
            )}

            <ToastContainer />

            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/reviews/*"
                element={
                  <PrivateRoute
                    element={<EmployeeReviewRoutes />}
                    roles={["user"]}
                  />
                }
              />
              <Route
                path="/agreements/*"
                element={
                  <PrivateRoute
                    element={<AgreementsRoutes />}
                    roles={["agreement"]}
                  />
                }
              />
              <Route
                path="/procurement/*"
                element={
                  <PrivateRoute
                    element={<ProcurementRoutes />}
                    roles={["procurement"]}
                  />
                }
              />
              <Route
                path="/inventory/*"
                element={
                  <PrivateRoute
                    element={<InventoryRoutes />}
                    roles={["procurement"]}
                  />
                }
              />
              <Route
                path="/bom/*"
                element={
                  <PrivateRoute
                    element={<BomRoutes />}
                    roles={["procurement"]}
                  />
                }
              />
              <Route
                path="/procurement-contracts/*"
                element={
                  <PrivateRoute
                    element={<ContractRoutes />}
                    roles={["procurement"]}
                  />
                }
              />
              <Route
                path="/property-register/*"
                element={
                  <PrivateRoute
                    element={<PropertyRegisterRoutes />}
                    roles={["propertyRegister"]}
                  />
                }
              />
              <Route
                path="/access-control/*"
                element={
                  <PrivateRoute
                    element={<AccessControlRoutes />}
                    roles={["user"]}
                  />
                }
              />
              <Route
                path="/payroll/*"
                element={
                  <PrivateRoute
                    element={<PayrollGenerator />}
                    roles={["payroll"]}
                  />
                }
              />
              <Route
                path="/plant-reports/*"
                element={
                  <PrivateRoute
                    element={<PlantReportsRoutes />}
                    roles={["admin"]}
                  />
                }
              />
              <Route
                path="/ai-assistant/*"
                element={
                  <PrivateRoute
                    element={<AiAssistant />}
                    roles={["cSuite", "admin"]}
                  />
                }
              />
              <Route
                path="/knowledge-base/*"
                element={
                  <PrivateRoute element={<KnowledgeBase />} roles={["user"]} />
                }
              />
              <Route
                path="/finance/*"
                element={
                  <PrivateRoute
                    element={<FinanceRoutes />}
                    roles={[
                      "finance",
                      "finance-manager",
                      "procurement",
                      "admin",
                    ]}
                  />
                }
              />
              <Route
                path="/rtp/*"
                element={
                  <PrivateRoute
                    element={<PettyCashPage />}
                    roles={[
                      "finance",
                      "finance-manager",
                      "procurement",
                      "admin",
                    ]}
                  />
                }
              />
              <Route
                path="/admin/users"
                element={
                  <PrivateRoute
                    element={<UserManagement />}
                    roles={["admin"]}
                  />
                }
              />
              <Route
                path="/admin/business-units"
                element={
                  <PrivateRoute
                    element={<BusinessUnitManagement />}
                    roles={["admin"]}
                  />
                }
              />
              {/* DISABLED: Staff Management module
              <Route
                path="/staff-management/*"
                element={
                  <PrivateRoute
                    element={<StaffManagementRoutes />}
                    roles={["staffManagement"]}
                  />
                }
              />
              */}
              <Route
                path="/wallboard/*"
                element={
                  <PrivateRoute
                    element={<WallboardRoutes />}
                    roles={["admin", "cSuite", "c-suite", "manager", "user"]}
                  />
                }
              />
              <Route
                path="/coms/*"
                element={
                  <PrivateRoute
                    element={<ComsRoutes />}
                    roles={["admin", "user"]}
                  />
                }
              />
            </Routes>
          </Suspense>
          <LoginModal />
          <Analytics />
        </VmsError>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

const Wrapper = () => {
  return (
    <Provider store={store}>
      <CssBaseline />
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <HttpsRedirect>
          <LocationSync />
          <App />
        </HttpsRedirect>
      </BrowserRouter>
    </Provider>
  );
};

export default Wrapper;
